import { configureIguazuRPC, IguazuRpcProceduresState, setProcedures } from 'iguazu-rpc';
import { createFaasProcedureConfig } from '@americanexpress/iguazu-procedures/utils/createFaasProcedureConfig';
import withStatusResponse from '@americanexpress/iguazu-procedures/utils/withStatusResponse';
import readUserSessionV1 from '@americanexpress/iguazu-procedures/readUserSessionV1';

import withLongerTimeout from './withLongerTimeout';
import createFaasProcedureConfigForServerSideRendering from './createFaasProcedureConfigForServerSideRendering';
import { ReduxState } from '../types';

const configureIguazuProcedures = () => {
  configureIguazuRPC({
    getToState: (state: ReduxState) =>
      state.getIn([
        'modules',
        'commercial-experience-root',
        'procedures',
      ]) as IguazuRpcProceduresState,
  });

  const createOneDataConfig = (functionName: string) =>
    withLongerTimeout(createFaasProcedureConfig(functionName));

  setProcedures({
    createSupplierExperienceLinkageV1: withStatusResponse(
      createOneDataConfig('CreateSupplierExperienceLinkage.v1')
    ),
    readSupplierExperienceUserProfileV1: withStatusResponse(
      createOneDataConfig('ReadSupplierExperienceUserProfile.v1')
    ),
    readSupplierExperienceAddressCollectionFormV1: createOneDataConfig(
      'ReadSupplierExperienceAddressCollectionForm.v1'
    ),
    readSupplierExperiencePaymentTermsV1: createOneDataConfig(
      'ReadSupplierExperiencePaymentTerms.v1'
    ),
    readSupplierExperiencePaymentTermsEligibilityV1: createOneDataConfig(
      'ReadSupplierExperiencePaymentTermsEligibility.v1'
    ),
    readSupplierPaymentVirtualPaymentTokenDetailsV1: createOneDataConfig(
      'ReadSupplierPaymentVirtualPaymentTokenDetails.v1'
    ),
    readSupplierExperienceAddressCollectionCompanyTabV1: createOneDataConfig(
      'ReadSupplierExperienceAddressCollectionCompanyTab.v1'
    ),
    readSupplierPaymentCommercialPaymentGatewayEmailV1:
      createFaasProcedureConfigForServerSideRendering(
        'ReadSupplierPaymentCommercialPaymentGatewayEmail.v1'
      ),
    readSupplierPaymentVirtualPaymentsPlatformEmailV1:
      createFaasProcedureConfigForServerSideRendering(
        'ReadSupplierPaymentVirtualPaymentsPlatformEmail.v1'
      ),
    ReadSupplierPaymentRemittanceReportV1: createFaasProcedureConfigForServerSideRendering(
      'ReadSupplierPaymentRemittanceReport.v1'
    ),
    CreateSupplierPaymentSessionDetailsUnauthV1: createFaasProcedureConfigForServerSideRendering(
      'CreateSupplierPaymentSessionDetailsUnauth.v1'
    ),
    createSupplierPaymentCommercialPaymentGatewayProfileV1:
      createFaasProcedureConfigForServerSideRendering(
        'CreateSupplierPaymentCommercialPaymentGatewayProfile.v1'
      ),
    CreateSupplierPaymentSessionDetailsAuthV1: createOneDataConfig(
      'CreateSupplierPaymentSessionDetailsAuth.v1'
    ),
    ReadAccountsReceivableAggregatedCustomersV1: createOneDataConfig(
      'ReadAccountsReceivableAggregatedCustomers.v1'
    ),
    ReadAccountsReceivableCustomerDetailsV1: createOneDataConfig(
      'ReadAccountsReceivableCustomerDetails.v1'
    ),
    ReadBuyerInitiatedPaymentsAggregatedRemittanceIdsV1: createOneDataConfig(
      'ReadBuyerInitiatedPaymentsAggregatedRemittanceIds.v1'
    ),
    ReadBuyerInitiatedPaymentsHistoricalSrafV1: withStatusResponse(
      createOneDataConfig('ReadBuyerInitiatedPaymentsHistoricalSraf.v1')
    ),
    ReadSupplierPaymentHistoryReportV1: withStatusResponse(
      createOneDataConfig('ReadSupplierPaymentHistoryReport.v1')
    ),
    ReadSupplierPaymentDetailsV1: createOneDataConfig('ReadSupplierPaymentDetails.v1'),
    ReadSupplierPaymentDetailsV2: createOneDataConfig('ReadSupplierPaymentDetails.v2'),
    ReadSupplierEmailTokenV1: createFaasProcedureConfigForServerSideRendering(
      'ReadSupplierEmailToken.v1'
    ),
    ReadSupplierEmailTokenV2: createFaasProcedureConfigForServerSideRendering(
      'ReadSupplierEmailToken.v2'
    ),
    ReadSupplierPaymentAggregatedPaymentsV4: createOneDataConfig(
      'ReadSupplierPaymentAggregatedPayments.v4'
    ),
    ReadSupplierPaymentRemittanceDetailsV1: createOneDataConfig(
      'ReadSupplierPaymentRemittanceDetails.v1'
    ),
    createSupplierExperienceUserInviteV1WithStatus: withStatusResponse(
      createOneDataConfig('CreateSupplierExperienceUserInvite.v1')
    ),
    createSupplierExperienceUserInviteLinkageV1WithStatus: withStatusResponse(
      createOneDataConfig('CreateSupplierExperienceUserInviteLinkage.v1')
    ),
    createSupplierExperienceProfileFromInviteV1: createFaasProcedureConfigForServerSideRendering(
      'CreateSupplierExperienceProfileFromInvite.v1'
    ),
    updateAccountsReceivableCustomerTermsAcknowledgementV1: createFaasProcedureConfig(
      'UpdateAccountsReceivableCustomerTermsAcknowledgement.v1'
    ),
    updateSupplierExperienceAddressCollectionV1: createOneDataConfig(
      'UpdateSupplierExperienceAddressCollection.v1'
    ),
    updateSupplierExperienceUserProfileV1WithStatus: withStatusResponse(
      createOneDataConfig('UpdateSupplierExperienceUserProfile.v1')
    ),
    updateSupplierExperiencePaymentTermsV1: createOneDataConfig(
      'UpdateSupplierExperiencePaymentTerms.v1'
    ),
    updateBuyerInitiatedPaymentsPaymentReversalV1: withStatusResponse(
      createOneDataConfig('UpdateBuyerInitiatedPaymentsPaymentReversal.v1')
    ),
    ReadSupplierPaymentAggregatedProfilesV1: createOneDataConfig(
      'ReadSupplierPaymentAggregatedProfiles.v1'
    ),
    ReadSupplierOutreachEmailTokenV1: createFaasProcedureConfigForServerSideRendering(
      'ReadSupplierOutreachEmailToken.v1'
    ),
    CreateSupplierOutreachResponseV1: createFaasProcedureConfigForServerSideRendering(
      'CreateSupplierOutreachResponse.v1'
    ),
    UpdateSupplierExperienceUserLastLoginV1: createOneDataConfig(
      'UpdateSupplierExperienceUserLastLogin.v1'
    ),
    CreateProfileWithCredentialsV2WithStatus: withStatusResponse(
      createOneDataConfig('CreateProfileWithCredentials.v2')
    ),
    ReadOneIdentityUserNameStatusV1WithStatus: withStatusResponse(
      createOneDataConfig('ReadOneIdentityUserNameStatus.v1')
    ),
    CreateAccountsReceivableUserAndSupplierProfileV1WithStatus: withStatusResponse(
      createOneDataConfig('CreateAccountsReceivableUserAndSupplierProfile.v1')
    ),
    ReadBuyerInitiatedPaymentsDealV1: createOneDataConfig('ReadBuyerInitiatedPaymentsDeal.v1'),
    CreateAccountsReceivableEnrollmentForBuyerInitiatedPayments: withStatusResponse(
      createOneDataConfig('CreateAccountsReceivableEnrollmentForBuyerInitiatedPayments.v1')
    ),
    ReadUserSessionV1WithStatus: readUserSessionV1,
    ReadSupplierPaymentAggregatedPaymentsStatusV1: createOneDataConfig(
      'ReadSupplierPaymentAggregatedPaymentsStatus.v1'
    ),
    ReadCommercialExperienceNotificationsV1: createOneDataConfig(
      'ReadCommercialExperienceNotifications.v1'
    ),
    CreateSupplierPaymentHistoryReportV1WithStatus: withStatusResponse(
      createOneDataConfig('CreateSupplierPaymentHistoryReport.v1')
    ),
    ReadSupplierPaymentHistoryReportV1WithStatus: withStatusResponse(
      createOneDataConfig('ReadSupplierPaymentHistoryReport.v1')
    ),
    UpdateCommercialExperienceNotificationByNotificationIdWithStatus: withStatusResponse(
      createOneDataConfig('UpdateCommercialExperienceNotificationByNotificationId.v1')
    ),
    ReadBuyerInitiatedPaymentsCustomersByRemittanceId: createOneDataConfig(
      'ReadBuyerInitiatedPaymentsCustomersByRemittanceId.v1'
    ),
    ReadBuyerInitiatedPaymentsRemittancesWithUserAccess: createOneDataConfig(
      'ReadBuyerInitiatedPaymentsRemittancesWithUserAccess.v1'
    ),
    ReadBuyerInitiatedPaymentsRemittanceIdsV1: createOneDataConfig(
      'ReadBuyerInitiatedPaymentsRemittanceIds.v1'
    ),
    UpdateBuyerInitiatedPaymentRemittanceNameWithStatus: withStatusResponse(
      createOneDataConfig('UpdateBuyerInitiatedPaymentRemittanceName.v1')
    ),
    UpdateBuyerInitiatedPaymentsRemitCustomersWithStatus: withStatusResponse(
      createOneDataConfig('UpdateBuyerInitiatedPaymentsRemitCustomers.v1')
    ),
    UpdateSupplierExperienceUserProfileRemittanceIdsWithStatus: withStatusResponse(
      createOneDataConfig('UpdateSupplierExperienceUserProfileRemittanceIds.v1')
    ),
    CreateBuyerInitiatedPaymentsRemittanceV1WithStatus: withStatusResponse(
      createOneDataConfig('CreateBuyerInitiatedPaymentsRemittance.v1')
    ),
    DeleteSupplierExperienceUserProfileForAuthorizedUserV1WithStatus: withStatusResponse(
      createOneDataConfig('DeleteSupplierExperienceUserProfileForAuthorizedUser.v1')
    ),
    CreateAccountsReceivableSupplierProfileExistingUserV1WithStatus: withStatusResponse(
      createOneDataConfig('CreateAccountsReceivableSupplierProfileExistingUser.v1')
    ),
  });
};

configureIguazuProcedures();

export default configureIguazuProcedures;
